import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import { Markdown, type MarkdownStorage } from 'tiptap-markdown';
import { cn } from '@/lib/utils';
import {
  Bold,
  Italic,
  Strikethrough,
  List,
  ListOrdered,
  Heading2,
  Heading3,
  Code,
} from 'lucide-react';

export interface WysiwygEditorProps {
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  /** Pixels, or any CSS length (e.g. `calc(100vh - 120px)`). */
  minHeight?: number | string;
  error?: boolean;
}

interface BubbleState {
  visible: boolean;
  top: number;
  left: number;
}

const WysiwygEditor: React.FC<WysiwygEditorProps> = ({
  value = '',
  onChange,
  onBlur,
  placeholder,
  className,
  disabled = false,
  minHeight = 200,
  error = false,
}) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const bubbleRef = useRef<HTMLDivElement>(null);
  const [bubble, setBubble] = useState<BubbleState>({ visible: false, top: 0, left: 0 });

  // The editor is created once; the Placeholder extension reads this ref on every
  // render so the placeholder follows prop and language changes.
  const placeholderRef = useRef<string>(placeholder ?? t('editor.placeholder'));
  placeholderRef.current = placeholder ?? t('editor.placeholder');

  const updateBubblePosition = useCallback(() => {
    if (disabled || !containerRef.current) {
      setBubble((prev) => ({ ...prev, visible: false }));
      return;
    }
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed || selection.rangeCount === 0) {
      setBubble((prev) => ({ ...prev, visible: false }));
      return;
    }
    const range = selection.getRangeAt(0);
    const rangeRect = range.getBoundingClientRect();
    const containerRect = containerRef.current.getBoundingClientRect();

    if (!rangeRect.width) {
      setBubble((prev) => ({ ...prev, visible: false }));
      return;
    }

    const bubbleWidth = bubbleRef.current?.offsetWidth ?? 260;
    const bubbleHeight = bubbleRef.current?.offsetHeight ?? 36;
    const GAP = 6;

    let top = rangeRect.top - containerRect.top - bubbleHeight - GAP;
    // If there's not enough space above, show below
    if (top < 0) {
      top = rangeRect.bottom - containerRect.top + GAP;
    }

    let left = rangeRect.left + rangeRect.width / 2 - containerRect.left - bubbleWidth / 2;
    // Clamp to container bounds
    const maxLeft = containerRect.width - bubbleWidth - 4;
    left = Math.max(4, Math.min(left, maxLeft));

    setBubble({ visible: true, top, left });
  }, [disabled]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bulletList: {},
        orderedList: {},
        heading: { levels: [1, 2, 3] },
      }),
      Placeholder.configure({
        placeholder: () => placeholderRef.current,
        emptyEditorClass: 'is-editor-empty',
      }),
      Markdown.configure({
        html: false,
        tightLists: true,
        bulletListMarker: '-',
        linkify: false,
        breaks: true,
        transformPastedText: true,
        transformCopiedText: false,
      }),
    ],
    content: value,
    editable: !disabled,
    onUpdate({ editor }) {
      if (onChange) {
        const mdStorage = editor.storage as unknown as { markdown: MarkdownStorage };
        onChange(mdStorage.markdown.getMarkdown());
      }
    },
    onBlur({ event }) {
      // Hide bubble when focus leaves the editor entirely
      const related = event.relatedTarget as Node | null;
      if (!bubbleRef.current?.contains(related)) {
        setBubble((prev) => ({ ...prev, visible: false }));
        onBlur?.();
      }
    },
    onSelectionUpdate() {
      // Small delay so the selection is stable in the DOM
      setTimeout(updateBubblePosition, 10);
    },
    editorProps: {
      attributes: {
        class: 'wysiwyg-editor-content outline-none',
      },
    },
  });

  // Sync external value changes
  useEffect(() => {
    if (!editor) return;
    const mdStorage = editor.storage as unknown as { markdown: MarkdownStorage };
    const currentMarkdown = mdStorage.markdown.getMarkdown();
    if (value !== currentMarkdown) {
      editor.commands.setContent(value || '');
    }
  }, [value, editor]);

  // Update editable state
  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled);
  }, [disabled, editor]);

  // Dismiss bubble on click outside
  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      if (
        bubbleRef.current &&
        !bubbleRef.current.contains(e.target as Node) &&
        !containerRef.current?.contains(e.target as Node)
      ) {
        setBubble((prev) => ({ ...prev, visible: false }));
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, []);

  const ToolbarButton = ({
    onClick,
    active,
    title,
    children,
  }: {
    onClick: () => void;
    active?: boolean;
    title: string;
    children: React.ReactNode;
  }) => (
    <button
      type="button"
      onMouseDown={(e) => {
        e.preventDefault();
        onClick();
        setTimeout(updateBubblePosition, 10);
      }}
      title={title}
      className={cn(
        'p-1 rounded transition-colors',
        active
          ? 'bg-primary text-primary-foreground'
          : 'text-popover-foreground hover:bg-accent'
      )}
    >
      {children}
    </button>
  );

  return (
    <div
      ref={containerRef}
      className={cn(
        'wysiwyg-editor relative w-full rounded-md border bg-background transition-colors',
        error ? 'border-destructive' : 'border-input',
        !disabled && 'focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 focus-within:ring-offset-background focus-within:border-ring',
        disabled && 'opacity-60 cursor-not-allowed',
        className
      )}
    >
      {/* Floating bubble menu (appears on text selection) */}
      {editor && !disabled && (
        <div
          ref={bubbleRef}
          style={{
            position: 'absolute',
            top: bubble.top,
            left: bubble.left,
            zIndex: 50,
            visibility: bubble.visible ? 'visible' : 'hidden',
            pointerEvents: bubble.visible ? 'auto' : 'none',
            opacity: bubble.visible ? 1 : 0,
            transition: 'opacity 0.1s',
          }}
          className="flex items-center gap-0.5 rounded-md border border-border bg-popover p-1 shadow-md"
        >
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBold().run()}
            active={editor.isActive('bold')}
            title={t('editor.bold')}
          >
            <Bold className="h-3.5 w-3.5" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleItalic().run()}
            active={editor.isActive('italic')}
            title={t('editor.italic')}
          >
            <Italic className="h-3.5 w-3.5" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleStrike().run()}
            active={editor.isActive('strike')}
            title={t('editor.strikethrough')}
          >
            <Strikethrough className="h-3.5 w-3.5" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleCode().run()}
            active={editor.isActive('code')}
            title={t('editor.inlineCode')}
          >
            <Code className="h-3.5 w-3.5" />
          </ToolbarButton>
          <div className="mx-0.5 h-4 w-px bg-border" />
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            active={editor.isActive('heading', { level: 2 })}
            title={t('editor.heading2')}
          >
            <Heading2 className="h-3.5 w-3.5" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            active={editor.isActive('heading', { level: 3 })}
            title={t('editor.heading3')}
          >
            <Heading3 className="h-3.5 w-3.5" />
          </ToolbarButton>
          <div className="mx-0.5 h-4 w-px bg-border" />
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            active={editor.isActive('bulletList')}
            title={t('editor.bulletList')}
          >
            <List className="h-3.5 w-3.5" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            active={editor.isActive('orderedList')}
            title={t('editor.orderedList')}
          >
            <ListOrdered className="h-3.5 w-3.5" />
          </ToolbarButton>
        </div>
      )}

      {/* The editable node is only as tall as its content, so clicks on the empty
          area below it would otherwise do nothing. */}
      <EditorContent
        editor={editor}
        style={{ minHeight: typeof minHeight === 'number' ? `${minHeight}px` : minHeight }}
        className={cn('px-3 py-2 text-sm text-foreground', !disabled && 'cursor-text')}
        onMouseDown={(e: React.MouseEvent<HTMLDivElement>) => {
          if (!disabled && e.target === e.currentTarget) {
            e.preventDefault();
            editor?.commands.focus('end');
          }
        }}
      />
    </div>
  );
};

export default WysiwygEditor;
